import React, { useRef } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { WidthProps } from 'styled-system';
import { themeGet, width } from 'styled-system';

import { InputType } from '../../../enums/input-type';
import { StateType } from '../../../enums/StateType';
import { useLocalization } from '../../../providers/LocaleProvider';
import type { FlexShrinkProps } from '../../../theme/system-utilities';
import { flexShrink } from '../../../theme/system-utilities';
import type { Button } from '../../Button';
import type { FormState } from '../../DynamicForm/types';
import { Flex } from '../../Flex';
import StatefulInput from '../../Input/StatefulInput';
import StateMessages from '../../Input/StatefulInput/StateMessages';
import type { InputProps } from '../../Input/StatefulInput/types';
import VisuallyHidden from '../../VisuallyHidden';
import { useMonthSelectOptions } from './hooks';
import SelectInput from './SelectInput';
import type { BirthDayFieldValue } from './types';

interface BirthdayFieldProps {
  formState: FormState<Record<string, BirthDayFieldValue>>;
  errorsTypes: Record<string, string>;
  submitButton: typeof Button;
  value: BirthDayFieldValue;
  className?: string;
  helpText?: React.ReactNode;
  deleteBirthdateBtn?: React.ReactNode;
  withStrongBorders?: boolean;
  onChange?: (date: { [key: string]: string }) => void;
}

const FieldsContainer = styled(Flex)`
  ${width};
`;

const InputContainer = styled.div<WidthProps & FlexShrinkProps>`
  ${width};
  ${flexShrink};
`;

const inputBorderStyle = props =>
  props.withStrongBorder &&
  css`
    :not([aria-invalid='true']) {
      border-color: ${props.theme.colors.sys.neutral.border.strong};
    }
  `;

const Input = styled(StatefulInput)<{ withStrongBorder: boolean }>`
  ${width};
  ${inputBorderStyle};

  &:hover {
    outline: 1px solid ${themeGet('colors.sys.neutral.border.hover')};
    border-color: ${themeGet('colors.sys.neutral.border.hover')};
  }
`;

const inputProps: InputProps = {
  mb: 'sp_0',
  type: InputType.Text,
  inputMode: 'numeric',
  labelStyles: {
    marginTop: '9px',
  },
};

const selectInputStyles = {
  ml: 'sp_8',
  mr: 'sp_8',
};

// eslint-disable-next-line complexity
const BirthdayField = ({
  onChange,
  formState,
  errorsTypes,
  value: values,
  helpText = null,
  deleteBirthdateBtn = null,
  withStrongBorders = false,
  submitButton: SubmitButton,
}: BirthdayFieldProps) => {
  const { t } = useLocalization();
  const dateRef = useRef(null);
  const monthOptions = useMonthSelectOptions();
  const monthLabel = t('h24_month');

  const { isSubmitting } = formState;
  const messages =
    errorsTypes &&
    Object.values(errorsTypes)?.map((message: string) => ({
      message,
      type: StateType.Error,
    }));

  const handleChange = (field: string, value: string) => {
    dateRef.current = {
      ...values,
      ...dateRef.current,
      [field]: value,
    };

    onChange(dateRef.current);
  };

  const areAllFieldsInvalid = errorsTypes
    ? Object.keys(errorsTypes).filter(key => !['day', 'month', 'year'].includes(key)).length > 0
    : false;

  return (
    <>
      <FieldsContainer width={1}>
        <InputContainer width={['60px', null, null, '72px']} flexShrink={0}>
          <Input
            {...inputProps}
            hasError={!!errorsTypes?.day || areAllFieldsInvalid}
            value={values.day ?? ''}
            label={t('h24_day')}
            maxLength={2}
            onChange={e => handleChange('day', e.target.value)}
            withStrongBorder={withStrongBorders}
            withIcon
          />
        </InputContainer>

        <label htmlFor={monthLabel}>
          <VisuallyHidden>{monthLabel}</VisuallyHidden>
        </label>
        <SelectInput
          id={monthLabel}
          hasError={!!errorsTypes?.month || areAllFieldsInvalid}
          value={values.month ?? ''}
          onChange={e => handleChange('month', e.target.value)}
          required
          options={[{ value: '', label: monthLabel, hidden: true }, ...monthOptions]}
          label={monthLabel}
          {...selectInputStyles}
          {...(withStrongBorders ? { borderColor: 'sys.neutral.border.strong' } : {})}
        />

        <InputContainer width={['74px', null, null, '78px']} flexShrink={0}>
          <Input
            {...inputProps}
            hasError={!!errorsTypes?.year || areAllFieldsInvalid}
            label={t('h24_year')}
            maxLength={4}
            value={values.year ?? ''}
            onChange={e => handleChange('year', e.target.value)}
            withStrongBorder={withStrongBorders}
            withIcon
          />
        </InputContainer>

        {!!SubmitButton && <SubmitButton isLoading={isSubmitting} />}
      </FieldsContainer>
      {messages?.length > 0 && <StateMessages withIcon messages={messages} />}
      {deleteBirthdateBtn}
      {helpText}
    </>
  );
};

export default BirthdayField;
